//ICS-209
import { useMemo } from 'react';
import dayjs from 'dayjs';
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

import { FormExportButton } from 'components/IAP/FormExportButton';
import { Spinner } from '@/components/ui/spinner';
import {
  OperationalPeriod,
  usePeriods,
  useIncident,
  useIncidentPermissions,
} from 'components/IAP/hooks/incidentHooks';
import { useIncidentInfo } from 'components/IAP/hooks/incidentInfoHooks';
import { useIncidentStatusSummaries } from 'components/IAP/hooks/incidentStatusSummaryHooks';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import FormVersioningBanner from 'components/IAP/femaCards/FormVersioningBanner';
import { useICSContext } from 'components/IAP/planningView/ICSContext';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import { filterItemsByOperationalPeriod } from 'utils/operationalPeriodUtils';

import { getIncidentCardTitle } from '../../USCGPage';
import { Ics204CardSection } from '../AssignmentList/ics204BoxLabels';
import { BoxInfo, PrintedValue } from '../icsBoxPrimitives';
import Ics209SignatureBox from './Ics209SignatureBox';
import IncidentStatusSummaryItem from './IncidentStatusSummaryItem';
import StatusSummaryVersionRecording from './statusSummaryVersionRecording';
import {
  ICS209_BOX_LABELS,
  ICS209_DATE_TIME_OF_REPORT_HINT,
  ICS209_INCIDENT_LOCATION_HINT,
  ICS209_INCIDENT_NAME_HINT,
  ICS209_OPERATIONAL_PERIOD_HINT,
} from './ics209BoxLabels';

type IncidentStatusSummaryProps = {
  currPeriod: OperationalPeriod | undefined;
  readOnly?: boolean;
};

const formatDateTime = (value: string | Date | undefined | null): string =>
  value && dayjs(value).isValid()
    ? dayjs(value).format('MM/DD/YYYY HH:mm')
    : '';

const IncidentStatusSummary: React.FC<IncidentStatusSummaryProps> = ({
  currPeriod,
  readOnly: forceReadOnly = false,
}) => {
  // TODO aeortic: Consider moving the resolution of incidentId to the hook itself
  const incidentId = useActualIncidentId();
  const {
    data: incidentStatusSummaries,
    isLoading,
    error,
  } = useIncidentStatusSummaries(incidentId || '');

  const { exportAsForm, isExporting } = useIcsFormPdfPreview();

  const { data: incidentPermissions } = useIncidentPermissions();
  const canEditCards = incidentPermissions?.['incident:editCards'] ?? false;
  const { effectivePeriodNumber, readOnlyForms } = useICSContext();
  const readOnly = forceReadOnly || !canEditCards || readOnlyForms;

  const incident = useIncident();
  const { data: incidentInfo } = useIncidentInfo();
  const { data: periods } = usePeriods();

  const filteredStatusSummary = useMemo(() => {
    return filterItemsByOperationalPeriod(
      incidentStatusSummaries || [],
      currPeriod,
      periods,
      (summary) => summary.createdAt
    );
  }, [incidentStatusSummaries, currPeriod, periods]);

  const handleExport = (format: 'pdf' | 'docx' = 'pdf') => {
    void exportAsForm(
      {
        formname: 'ICS_209_CG',
        data: {},
        mapSketch: null,
        periodNumber: currPeriod?.period_number,
        format,
      },
      { allowWorkspaceDownload: !!filteredStatusSummary }
    );
  };

  const exportActions = (
    <FormExportButton
      formname="ICS_209_CG"
      onExport={handleExport}
      label="Preview ICS-209"
      disabled={isExporting}
    />
  );

  // Box 4 looks ahead: the report covers the period that follows the one it is
  // filed against, which is what the PDF export prints.
  const currPeriodNumber = currPeriod?.period_number;
  const nextPeriod = useMemo(() => {
    if (currPeriodNumber === undefined) return undefined;
    return (periods ?? []).find(
      (period) => period.period_number === currPeriodNumber + 1
    );
  }, [periods, currPeriodNumber]);

  const nextPeriodRange = [
    formatDateTime(nextPeriod?.start_date_time),
    formatDateTime(nextPeriod?.end_date_time),
  ]
    .filter(Boolean)
    .join(' to ');

  const headerBoxes = (
    <div className="tw:grid tw:gap-3 tw:md:grid-cols-2 tw:xl:grid-cols-4">
      <Ics204CardSection
        boxLabel={ICS209_BOX_LABELS.incidentName}
        testId="ics209-section-incident-name"
        className="tw:mt-0 tw:h-full tw:min-w-0"
        headerAction={
          <BoxInfo label="About the incident name">
            {ICS209_INCIDENT_NAME_HINT}
          </BoxInfo>
        }
      >
        <PrintedValue
          value={incident.data?.name ?? ''}
          testId="ics209-field-incident-name"
        />
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS209_BOX_LABELS.incidentLocation}
        testId="ics209-section-incident-location"
        className="tw:mt-0 tw:h-full tw:min-w-0"
        headerAction={
          <BoxInfo label="About the incident location">
            {ICS209_INCIDENT_LOCATION_HINT}
          </BoxInfo>
        }
      >
        <PrintedValue
          value={incidentInfo?.incidentLocation || incident.data?.address || ''}
          testId="ics209-field-incident-location"
        />
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS209_BOX_LABELS.dateTimeOfReport}
        testId="ics209-section-date-time-of-report"
        className="tw:mt-0 tw:h-full tw:min-w-0"
        headerAction={
          <BoxInfo label="About the date and time of report">
            {ICS209_DATE_TIME_OF_REPORT_HINT}
          </BoxInfo>
        }
      >
        <PrintedValue
          value={formatDateTime(filteredStatusSummary?.createdAt)}
          testId="ics209-field-date-time-of-report"
        />
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS209_BOX_LABELS.operationalPeriodCovered}
        testId="ics209-section-operational-period"
        className="tw:mt-0 tw:h-full tw:min-w-0"
        headerAction={
          <BoxInfo label="About the operational period covered">
            {ICS209_OPERATIONAL_PERIOD_HINT}
          </BoxInfo>
        }
      >
        <PrintedValue
          value={nextPeriodRange}
          testId="ics209-field-operational-period"
        />
      </Ics204CardSection>
    </div>
  );

  const body = (
    <StatusSummaryVersionRecording>
      <div className="tw:flex tw:flex-col tw:gap-3">
        <FormVersioningBanner baseId="ICS_209" />
        {headerBoxes}
        {isLoading ? (
          <div className="tw:flex tw:justify-center tw:py-4">
            <Spinner aria-label="Loading incident status summaries" />
          </div>
        ) : null}
        {error ? (
          <div
            role="alert"
            className="tw:rounded-md tw:border tw:border-destructive tw:p-3 tw:text-sm tw:text-destructive"
          >
            Error loading incident status summaries: {error.message}
          </div>
        ) : null}
        {!isLoading && !error ? (
          <IncidentStatusSummaryItem
            statusSummary={filteredStatusSummary}
            readOnly={readOnly}
          />
        ) : null}
        <Ics209SignatureBox periodNumber={effectivePeriodNumber} />
      </div>
    </StatusSummaryVersionRecording>
  );

  return (
    <Card
      data-testid="uscg-209-card"
      data-tour="ics209-card"
      data-form-family="uscg"
      className="dark tw:bg-background tw:text-foreground"
    >
      <CardHeader>
        <CardTitle>{getIncidentCardTitle('incidentStatusSummary')}</CardTitle>
        <CardAction>{exportActions}</CardAction>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
};

export default IncidentStatusSummary;
